import { AutofocusDirective } from '../../../../../../shared/directive/autofocus.directive';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import isEqual from 'lodash/isEqual';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'anihis-clinical-examination-report',
  standalone: true,
  imports: [
    AutofocusDirective,
    CommonModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  templateUrl: './clinical-examination-report.component.html',
  styleUrl: './clinical-examination-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicalExaminationReportComponent
  extends FormBaseComponent
  implements OnInit
{
  @Output() formChange = new EventEmitter<any>();

  constructor() {
    super();
    this.form = this.fb.group({
      previousHistory: this.fb.group({
        previousHistoryNote: new FormControl(''),
        anamnesisNote: new FormControl(''),
      }),

      trijas: this.fb.group({
        temperature: new FormControl(''),
        pulse: new FormControl(''),
        breathingFrequency: new FormControl(''),
        capillaryRefill: new FormControl(''),
      }),

      overallImpression: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        tm: new FormControl(undefined),
        skinny: new FormControl(false),
        normallyFed: new FormControl(false),
        obese: new FormControl(false),
        descriptionAbnormality: new FormControl(''),
      }),

      skin: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        reduced: new FormControl(false),
        turgorNormal: new FormControl(false),
        myxedema: new FormControl(false),
        conditionCoat: new FormControl(false),
        paranalSacs: new FormControl(false),
        anocutaneousTransition: new FormControl(false),
        nailsInterdigital: new FormControl(false),
        ectoparasites: new FormControl(false),
        tumors: new FormControl(false),
        skinLesions: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      mucousMembranes: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        pink: new FormControl(false),
        pale: new FormControl(false),
        cyanotic: new FormControl(false),
        icteric: new FormControl(false),
        hyperemic: new FormControl(false),
        petechiae: new FormControl(false),
        erosions: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      lymphNodes: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        descriptionAbnormality: new FormControl(''),
      }),

      heartCirculation: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        descriptionAbnormality: new FormControl(''),
      }),

      respiratoryTract: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),
        inspirational: new FormControl(false),

        expiratory: new FormControl(false),
        mixed: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      digestiveTractAbdomen: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      urogenitalTract: this.fb.group({
        waterConsumptionReduced: new FormControl(false),
        waterConsumptionNormal: new FormControl(false),
        waterConsumptionIncreased: new FormControl(false),
        urineProductionReduced: new FormControl(false),
        urineProductionNormal: new FormControl(false),
        urineProductionIncreased: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      nervousLocomotorSystem: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      ears: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      eyes: this.fb.group({
        normal: new FormControl(false),
        abnormal: new FormControl(false),

        descriptionAbnormality: new FormControl(''),
      }),

      recommendedAnalysis: this.fb.group({
        hematology: new FormControl(false),
        biochemistry: new FormControl(false),
        xRay: new FormControl(false),
        ekg: new FormControl(false),
        echo: new FormControl(false),
        mriCt: new FormControl(false),
        biopsy: new FormControl(false),
        endoscopy: new FormControl(false),
        urineAnalysis: new FormControl(false),
        other: new FormControl(''),
      }),

      listProblems: this.fb.group({
        problem1: new FormControl(''),
        problem2: new FormControl(''),
        problem3: new FormControl(''),
        problem4: new FormControl(''),
        problem5: new FormControl(''),
        problem6: new FormControl(''),
        problem7: new FormControl(''),
        problem8: new FormControl(''),
        problem9: new FormControl(''),
      }),

      therapy: this.fb.group({
        workingDiagnosis: new FormControl(''),
        therapy: new FormControl(''),
        veterinarianName: new FormControl(''),
      }),
    });
  }

  override ngOnInit(): void {
    this.subscribeToFormValueChanges(this.form);
  }

  private subscribeToFormValueChanges(form: FormGroup) {
    form.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(isEqual))
      .subscribe((value) => this.formChange.emit(value));
  }
}
